import { useParams } from 'react-router'
import 'survey-core/defaultV2.min.css'
import Loader from '../../common/Loader/index.tsx'
import { getEmployeeResponse, getManagerResponse, isAssessmentFilledOutByManager, isAssessmentFilledOutByUser } from '../../utils.ts'
import { generateChartData, IMergedAnswerStatistic, mergeResponses } from './utils.ts'
import { RadarComponent } from '../../components/Charts/Radar.tsx'
import { IAssessment, IAssessmentResponse, IUser } from '../../services/nextpeakClient'
import { useGetAssessment, useGetUserV2 } from '../../http/hooks.ts'
import { getMe } from '../../MeProvider.ts'
import { Page, PageProps } from '../../layout/Page.tsx'
import Card from '../../newComponents/Card/Card.tsx'
import Table, { validateTableProps } from '../../newComponents/Table/index.tsx'
import { TableDataCell, TableHeaderCell } from '../../newComponents/Table/Cell.tsx'
import ProgressBar from '../../newComponents/ProgressBar/index.tsx'
import { formatQuestionText } from '../../surveyControl/mappers/assessments.tsx'
import { useNextPeakClient } from '../../services/nextpeakClient/clientProvider.tsx'
import { Heading2, Heading3 } from '../../newComponents/Headings/index.tsx'

const AnswersTable = ({ answers, employee }: { answers: IMergedAnswerStatistic[]; employee: IUser }) => {
	const me = getMe()
	const iAmEmployee = me.id === employee.id

	const tableProps = validateTableProps(
		[
			{
				key: 'question',
				content: <TableHeaderCell border="bottom">Question</TableHeaderCell>,
			},
			{
				key: 'managerAnswerValue',
				content: <TableHeaderCell border="bottom">Manager</TableHeaderCell>,
			},
			{
				key: 'employeeAnswerValue',
				content: <TableHeaderCell border="bottom">Employee</TableHeaderCell>,
			},
		] as const,
		answers.map((answer) => ({
			question: (
				<TableDataCell border="bottom">
					{formatQuestionText(
						iAmEmployee ? answer.question.subordinateText : answer.question.managerText,
						employee.name,
						me.activeOrg?.name!
					)}
				</TableDataCell>
			),
			managerAnswerValue: <TableDataCell border="bottom">{answer.managerAnswerValue}</TableDataCell>,
			employeeAnswerValue: <TableDataCell border="bottom">{answer.employeeAnswerValue}</TableDataCell>,
		}))
	)

	return <Table {...tableProps} />
}

interface ChartsProps {
	employeeResponse: IAssessmentResponse
	managerResponse: IAssessmentResponse
	assessment: IAssessment
}

const Charts = ({ employeeResponse, managerResponse, assessment }: ChartsProps) => {
	const client = useNextPeakClient()
	const { data, isLoading } = useGetUserV2(client, { userId: employeeResponse.userId })

	if (isLoading && !data) return <Loader />

	if (!data) return null

	const result = mergeResponses(employeeResponse, managerResponse)
	const chartData = generateChartData(assessment)

	return (
		<>
			<div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-4 2xl:gap-7.5">
				<Card className="col-span-2">
					<div className="flex flex-col justify-between h-full">
						<Heading2
							size="lg"
							className="mb-0.5 text-center"
						>{`${assessment.assessmentType!.position!.role} ${assessment.assessmentType!.position!.level}`}</Heading2>
						<p className="text-sm font-medium text-center">Assessment type</p>
					</div>
				</Card>
				<Card>
					<div className="flex flex-col justify-between h-full">
						<Heading2 size="lg" className="mb-0.5 text-center">
							{result.managerAverageScore} %
						</Heading2>
						<p className="text-sm font-medium text-center">Manager score</p>
					</div>
				</Card>
				<Card>
					<div className="flex flex-col justify-between h-full">
						<Heading2 size="lg" className="mb-0.5 text-center">
							{result.employeeAverageScore} %
						</Heading2>
						<p className="text-sm font-medium text-center">Employee score</p>
					</div>
				</Card>
				<Card className="col-span-2 flex flex-col justify-center">
					<div className="border-stroke p-4 dark:border-strokedark">
						{result.averageByCategory.map((category) => (
							<div key={category.category} className="items-center sm:flex mb-2">
								<div className="flex w-full max-w-42.5 items-center gap-3.5">
									<p className="font-medium text-black dark:text-white">{category.category}</p>
								</div>
								<ProgressBar progress={category.managerAverageScore} />
							</div>
						))}
					</div>
				</Card>
				<Card className="col-span-2">
					<RadarComponent chartData={chartData} square={false} />
				</Card>
			</div>
			{result.averageByCategory.map((category) => (
				<Card key={category.category}>
					<Heading3 size="md" className="mb-7">
						{category.category}
					</Heading3>
					{category.subcategories.map((subcategory) => (
						<Card isRoot={false} key={subcategory.category} className="my-3">
							<div className="flex justify-between items-center mb-7">
								<Heading2 size="md">{subcategory.category}</Heading2>
								<div className="flex items-center">
									<p className="text-sm font-medium text-center">Awarded score: </p>
									<Heading3 size="md" className="ml-3 text-center">
										{subcategory.managerAverageScore} %
									</Heading3>
								</div>
							</div>
							<AnswersTable answers={subcategory.answers} employee={data} />
						</Card>
					))}
				</Card>
			))}
		</>
	)
}

const AssessmentDetail = () => {
	const { assessmentId } = useParams()

	const pageProps: PageProps = {
		title: 'Assessment',
		breadcrumbs: 'Assessment',
	}

	if (!assessmentId) {
		return (
			<Page {...pageProps}>
				<div className="flex flex-col gap-10">
					<Loader />
				</div>
			</Page>
		)
	}

	const client = useNextPeakClient()
	const { data: assessment, isLoading } = useGetAssessment(client, {
		assessmentId,
		includes: [
			'assessmentType',
			'assessmentType.position',
			'assessmentResponses',
			'assessmentResponses.answers',
			'assessmentResponses.answers.question',
		],
	})

	if (!assessment && isLoading) {
		return (
			<Page {...pageProps}>
				<div className="flex flex-col gap-10">
					<Loader />
				</div>
			</Page>
		)
	}

	if (!assessment && !isLoading) {
		return (
			<Page {...pageProps}>
				<div className="flex flex-col gap-10">
					<div>Assessment not found</div>
				</div>
			</Page>
		)
	}

	const isFilledOutByEmployee = isAssessmentFilledOutByUser(assessment!)
	const isFilledOutByManager = isAssessmentFilledOutByManager(assessment!)

	if (!isFilledOutByEmployee || !isFilledOutByManager) {
		return (
			<Page {...pageProps}>
				<div className="flex flex-col gap-10">
					<div>Waiting for all parties to fill out the assessment first</div>
				</div>
			</Page>
		)
	}

	return (
		<Page {...pageProps}>
			<div className="flex flex-col gap-10">
				{isFilledOutByEmployee && isFilledOutByManager && (
					<Charts
						assessment={assessment!}
						employeeResponse={getEmployeeResponse(assessment!)!}
						managerResponse={getManagerResponse(assessment!)!}
					/>
				)}
			</div>
		</Page>
	)
}

export default AssessmentDetail
